/**
 * GenerateListDialog.jsx — Erzeugt EIN Dokument mit einer Mitarbeiter-Liste.
 *
 * Filter: Scope (Firma/Niederlassung/Standort) + Abteilung + nur aktive MA.
 * Ergebnis: ein Download (DOCX oder XLSX).
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Loader2, Download, Users } from 'lucide-react';
import api, { branchesAPI, locationsAPI, employeesAPI } from '../../lib/api';
import { useOrgLabels } from '../../contexts/OrgLabelsContext';
import { toast } from 'sonner';

export const GenerateListDialog = ({ open, onOpenChange, template }) => {
  const orgL = useOrgLabels();
  const [scope, setScope] = useState('company');
  const [scopeId, setScopeId] = useState('');
  const [department, setDepartment] = useState('all');
  const [activeOnly, setActiveOnly] = useState(true);
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setScope('company');
    setScopeId('');
    setDepartment('all');
    setActiveOnly(true);

    branchesAPI.getAll().then((r) => setBranches(r.data || [])).catch(() => {});
    locationsAPI.getAll().then((r) => setLocations(r.data || [])).catch(() => {});
    employeesAPI.getAll({ is_active: true }).then((r) => {
      const set = new Set();
      (r.data || []).forEach((e) => { if (e.department) set.add(e.department); });
      setDepartments(Array.from(set).sort());
    }).catch(() => {});
  }, [open]);

  const handleGenerate = async () => {
    if (!template) return;
    if (scope !== 'company' && !scopeId) {
      toast.error(scope === 'branch' ? 'Bitte Niederlassung wählen' : 'Bitte Standort wählen');
      return;
    }
    setBusy(true);
    try {
      const body = {
        scope,
        scope_id: scope === 'company' ? null : scopeId,
        department: department === 'all' ? null : department,
        active_only: activeOnly,
      };
      const res = await api.post(`/templates/${template.id}/generate-list`, body);
      const file = res.data;
      const bytes = Uint8Array.from(atob(file.content_base64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], {
        type: file.file_type === 'docx'
          ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Liste mit ${file.count} Mitarbeiter${file.count === 1 ? '' : 'n'} generiert (${file.scope})`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Listen-Generierung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-4 sm:p-6" data-testid="generate-list-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-base sm:text-lg">
            <Users className="h-5 w-5 text-purple-600" />
            Liste generieren
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            {template?.name && <>Vorlage: <strong>{template.name}</strong> · </>}
            Erzeugt EIN Dokument mit allen Mitarbeitern des gewählten Bereichs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Bereich</Label>
            <Select value={scope} onValueChange={(v) => { setScope(v); setScopeId(''); }}>
              <SelectTrigger data-testid="list-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="company">Gesamte Firma</SelectItem>
                <SelectItem value="branch">{orgL.branch}</SelectItem>
                <SelectItem value="location">{orgL.location}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {scope !== 'company' && (
            <div className="space-y-1.5">
              <Label className="text-xs">{scope === 'branch' ? orgL.branch : orgL.location}</Label>
              <Select value={scopeId} onValueChange={setScopeId}>
                <SelectTrigger data-testid="list-scope-id">
                  <SelectValue placeholder={scope === 'branch' ? `${orgL.branch} wählen…` : `${orgL.location} wählen…`} />
                </SelectTrigger>
                <SelectContent>
                  {(scope === 'branch' ? branches : locations).map((it) => (
                    <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">Abteilung (optional)</Label>
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger data-testid="list-department">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Abteilungen</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between border-t border-border/40 pt-3">
            <Label htmlFor="active-only" className="text-sm">Nur aktive Mitarbeiter</Label>
            <Switch
              id="active-only"
              checked={activeOnly}
              onCheckedChange={setActiveOnly}
              data-testid="list-active-only"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" className="w-full sm:w-auto" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button className="w-full sm:w-auto" onClick={handleGenerate} disabled={busy} data-testid="list-generate-confirm">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Liste erzeugen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateListDialog;
